<template>
	<BaseButton
		:classes="buttonClasses"
		:disabled="props.disabled || !thirdPartyAuthStore.isAppleLibraryLoaded"
		:is-working="props.isWorking"
		data-test="apple-auth-button"
		@click="handleClick"
		><BaseSvgIcon v-if="!props.isWorking" class="mr-50" :name="logoName" height="24" width="24" />Continue with
		Apple</BaseButton
	>
</template>

<script lang="ts">
export default {
	name: 'AppleAuthButton'
};
</script>

<script setup lang="ts">
import { AppleAuthParameters, AppleOAuthResponse, ThirdPartyAuthTheme } from 'types/third-party-auth';
import { computed } from 'vue';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';
import { v4 as uuidv4 } from 'uuid';

interface Props {
	disabled?: boolean;
	isWorking?: boolean;
	theme?: ThirdPartyAuthTheme;
}

interface Emits {
	(e: 'apple-auth-payload', value: AppleAuthParameters): void;
}

const props = withDefaults(defineProps<Props>(), {
	disabled: false,
	isWorking: false,
	theme: 'PRIMARY'
});

const emits = defineEmits<Emits>();

const thirdPartyAuthStore = useThirdPartyAuthStore();

const buttonClasses = computed((): string => {
	return `button ${props.theme === 'PRIMARY' || props.theme === 'TERTIARY' ? 'button-default-third-party-auth-reversed' : 'button-default-third-party-auth'} no-wrap width-100`;
});

const logoName = computed((): string =>
	props.theme === 'PRIMARY' || props.theme === 'TERTIARY' ? 'logo-apple-black' : 'logo-apple-white'
);

async function handleClick(): Promise<void> {
	try {
		const nonce = uuidv4();
		window.AppleID.auth.init({
			clientId: import.meta.env.VITE_APPLE_SIGN_UP_ID,
			scope: 'name email',
			redirectURI: import.meta.env.VITE_BASE_URL,
			state: 'Initial user authentication request',
			usePopup: true,
			nonce
		});

		const authResponse: AppleOAuthResponse = await window.AppleID.auth.signIn();
		const authPayload: AppleAuthParameters = {
			authCode: authResponse.authorization?.code ?? '',
			idToken: authResponse.authorization?.id_token ?? '',
			redirectUri: import.meta.env.VITE_BASE_URL,
			appleClientId: import.meta.env.VITE_APPLE_SIGN_UP_ID,
			fundriseClientId: import.meta.env.VITE_API_CLIENT_ID,
			appleFirstName: authResponse.user?.name.firstName ?? '',
			appleLastName: authResponse.user?.name.lastName ?? '',
			nonce
		};

		emits('apple-auth-payload', authPayload);
	} catch {}
}
</script>
